import type { ListInput } from '../api/client';
import { text, type LocalizedText } from '../app/i18n';
import type { Entry } from './form-targets';

/**
 * Sélecteur d'objet d'un champ `reference` du gabarit (inspection-template-body 1.2.0,
 * `presentation.selector`, ADR-141 §1.2) : objets de `field.reference_type` lus par la liste
 * d'objets de l'API avec le filtre déclaré, libellé et code affichés, recherche. Même règles que la
 * tablette (`apps/operator/lib/features/task/presentation/reference_field.dart`) : libellé
 * « code — libellé », recherche sans casse sur ce libellé (au serveur, `q` d'ADR-094, quand la
 * liste est longue), l'identifiant choisi est la réponse, effacement sur un champ facultatif.
 * Affichage seulement : la commande et la politique vérifient la référence au serveur.
 */

/** Forme de `presentation.selector` (contrat 1.2.0). */
export interface ObjectSelectorSpec {
  /** Filtre de la liste d'objets : `{ champ: { opérateur: valeur } }`, opérateurs gouvernés par le serveur. */
  filter?: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  label_field?: string;
  code_field?: string;
  /** Recherche proposée (vrai par défaut). */
  search?: boolean;
}

export type SelectorRow = Readonly<Record<string, unknown>> & { id?: unknown };

/** Nombre d'objets lus, comme la tablette ; au-delà, le filtre du gabarit doit restreindre la liste. */
export const SELECTOR_LIMIT = 200;

/** `presentation.selector` d'une entrée, ou un sélecteur sans filtre. */
export function selectorOf(entry: Entry): ObjectSelectorSpec {
  return entry.presentation?.selector ?? {};
}

function shown(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'object') return text(value as LocalizedText, 'fr', '');
  return String(value);
}

/** Libellé d'un objet : « code — libellé » ; libellé = `label_field`, sinon `display_name`, `name`, l'identifiant. */
export function selectorLabel(row: SelectorRow, selector: ObjectSelectorSpec): string {
  const code = selector.code_field === undefined ? undefined : shown(row[selector.code_field]);
  const label = shown(row[selector.label_field ?? 'display_name']) ?? shown(row['display_name']) ?? shown(row['name']) ?? shown(row.id) ?? '';
  return [...(code !== undefined && code !== '' ? [code] : []), label].join(' — ');
}

/** Un objet répond-il à la recherche ? Sans casse, sur le libellé affiché ; une recherche vide garde tout. */
export function selectorMatches(row: SelectorRow, selector: ObjectSelectorSpec, query: string): boolean {
  return query === '' || selectorLabel(row, selector).toLowerCase().includes(query.toLowerCase());
}

/** Longueur maximale d'une recherche acceptée par le serveur (ADR-094). */
export const SELECTOR_SEARCH_MAX = 100;

/**
 * Requête de la liste d'objets : le filtre déclaré (une liste devient « a,b »), au plus
 * {@link SELECTOR_LIMIT} objets ; avec une recherche (liste longue), le paramètre `q` d'ADR-094,
 * saisie rognée et coupée à {@link SELECTOR_SEARCH_MAX} caractères. Le serveur cherche, sans casse,
 * sur les champs déclarés cherchables que l'utilisateur peut lire. Dans un formulaire de tâche,
 * `work_item_id` borne la liste à l'entité de la tâche (ADR-141, addendum (2)) ; hors tâche, rien.
 */
export function selectorQuery(selector: ObjectSelectorSpec, search?: string, workItemId?: string): ListInput {
  const filter: Record<string, Record<string, string>> = {};
  for (const [field, operators] of Object.entries(selector.filter ?? {})) {
    filter[field] = Object.fromEntries(Object.entries(operators).map(([operator, value]) => [operator, Array.isArray(value) ? value.map(String).join(',') : String(value)]));
  }
  const q = (search ?? '').trim().slice(0, SELECTOR_SEARCH_MAX);
  const query: ListInput = q === '' ? { filter, limit: SELECTOR_LIMIT } : { filter, limit: SELECTOR_LIMIT, q };
  // Dans une tâche, la liste ne propose que les objets que la commande acceptera depuis l'entité de la tâche.
  if (workItemId !== undefined && workItemId !== '') query.work_item_id = workItemId;
  return query;
}

/** Liste longue : le serveur a d'autres objets que les {@link SELECTOR_LIMIT} lus ; la recherche l'interroge alors. */
export function selectorTruncated(page: { next_cursor?: string } | undefined): boolean {
  return typeof page?.next_cursor === 'string' && page.next_cursor !== '';
}

/** Le choix s'efface seulement sur un champ facultatif qui a une valeur ; jamais sur un champ obligatoire. */
export function selectorClearable(required: boolean, value: unknown): boolean {
  return !required && value !== null && value !== undefined && value !== '';
}

/** Une ligne du résumé d'un objet choisi ; `value` absente : montrée « Manquant » (encart « Détails du chasseur » du v1). */
export interface SelectorDetail {
  kind: 'address' | 'phone' | 'email';
  value: string | null;
}

/**
 * Carte d'un objet de sélecteur (refonte du 2026-10-19, d'après l'écran v1 « Réception du gibier ») :
 * le nom, un sous-titre « numéro · code postal commune » lu dans les champs du Core (`code_field`,
 * première valeur de `legal_identifiers`, première adresse), les initiales de l'avatar et le résumé
 * (adresse, téléphone, courriel). Mêmes règles que la tablette (`selectorCard` de
 * `object_selector.dart`), jeu partagé `selector-card.cases.json`. Jamais l'identifiant technique.
 */
export interface SelectorCard {
  title: string;
  subtitle: string;
  initials: string;
  details: SelectorDetail[];
}

const textOf = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() !== '' ? value.trim() : typeof value === 'number' ? String(value) : undefined);
const firstRecord = (value: unknown): Record<string, unknown> | undefined => (Array.isArray(value) ? (value.find((entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry)) as Record<string, unknown> | undefined) : undefined);

/** Initiales d'un nom : deux lettres, des deux premiers mots (« Alsace Vosges » → « AV »), sinon les deux premières. */
export function selectorInitials(name: string): string {
  const words = name.trim().split(/[\s\-'’]+/).filter((word) => word !== '' && /[A-Za-zÀ-ÿ0-9]/.test(word));
  if (words.length === 0) return '?';
  if (words.length === 1) return [...words[0]!].slice(0, 2).join('').toUpperCase();
  return ([...words[0]!][0]! + [...words[1]!][0]!).toUpperCase();
}

export function selectorCard(row: SelectorRow, selector: ObjectSelectorSpec): SelectorCard {
  const title = shown(row[selector.label_field ?? 'display_name']) ?? shown(row['display_name']) ?? shown(row['name']) ?? '';
  const identifiers = row['legal_identifiers'];
  const fromIdentifiers = identifiers !== null && typeof identifiers === 'object' && !Array.isArray(identifiers) ? Object.values(identifiers).map(textOf).find((value) => value !== undefined) : undefined;
  const number = (selector.code_field === undefined ? undefined : textOf(row[selector.code_field])) ?? fromIdentifiers;
  const address = firstRecord(row['addresses']);
  const place = address === undefined ? undefined : [textOf(address['postal_code']), textOf(address['city'])].filter((part): part is string => part !== undefined).join(' ');
  const points = Array.isArray(row['contact_points']) ? (row['contact_points'] as unknown[]).filter((point): point is Record<string, unknown> => point !== null && typeof point === 'object') : [];
  const channel = (channels: string[]): string | null => points.filter((point) => channels.includes(String(point['channel']))).map((point) => textOf(point['value'])).find((value) => value !== undefined) ?? null;
  const lines = address === undefined ? [] : [...(Array.isArray(address['lines']) ? (address['lines'] as unknown[]).map(textOf).filter((line): line is string => line !== undefined) : []), ...(place !== undefined && place !== '' ? [place] : [])];
  return {
    title,
    subtitle: [...(number === undefined ? [] : [number]), ...(place !== undefined && place !== '' ? [place] : [])].join(' · '),
    initials: selectorInitials(title),
    details: [
      { kind: 'address', value: lines.length === 0 ? null : lines.join(', ') },
      { kind: 'phone', value: channel(['mobile', 'phone']) },
      { kind: 'email', value: channel(['email']) },
    ],
  };
}

/** Sans casse ni accents, pour la recherche (« Étang » trouve « etang »). */
function fold(value: string): string {
  const from = 'àâäáãåçéèêëíìîïñóòôöõúùûüýÿœæ';
  const to = 'aaaaaaceeeeiiiinooooouuuuyyoa';
  return [...value.toLowerCase()].map((char) => (from.includes(char) ? to[from.indexOf(char)]! : char)).join('');
}

/** La recherche porte aussi sur le sous-titre de la carte (numéro, commune) parmi les objets lus ; chaque mot doit y être. */
export function selectorCardMatches(row: SelectorRow, selector: ObjectSelectorSpec, query: string): boolean {
  if (query.trim() === '') return true;
  const haystack = fold(`${selectorLabel(row, selector)} ${selectorCard(row, selector).subtitle}`);
  return fold(query).split(/\s+/).filter((word) => word !== '').every((word) => haystack.includes(word));
}
